'use client';

import React, { useMemo, useState } from 'react';
import { Sample } from '@/sharedTypes/Api';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { SearchInput } from '@/components/ui/search-input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  getComposerCounts,
  getAvailableLetters,
  extractLetter,
} from '@/utils/composerUtils';

type ComposerFilterGroupProps = {
  samples: Sample[];
  selectedComposer: string | null;
  onComposerChange: (composer: string | null) => void;
};

export const ComposerFilterGroup = ({
  samples,
  selectedComposer,
  onComposerChange,
}: ComposerFilterGroupProps) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.toLowerCase().startsWith('ru') ? 'ru' : 'en') as 'en' | 'ru';

  // Internal state for search and letter filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  // Memoize composer counts (counts ALL samples passed in, backend already filters)
  const composerCounts = useMemo(
    () => getComposerCounts(samples, lang),
    [samples, lang]
  );

  // Get array of composer names
  const composers = useMemo(
    () => Array.from(composerCounts.keys()),
    [composerCounts]
  );

  // Memoize available letters
  const availableLetters = useMemo(
    () => getAvailableLetters(composers, lang),
    [composers, lang]
  );

  // Filter composers by search query OR selected letter
  const filteredComposers = useMemo(() => {
    let filtered = composers;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((composer) =>
        composer.toLowerCase().includes(query)
      );
    }
    // Filter by selected letter (only if no search query)
    else if (selectedLetter) {
      filtered = filtered.filter((composer) => {
        const letter = extractLetter(composer, lang);
        return letter === selectedLetter;
      });
    }

    // Sort alphabetically
    filtered.sort((a, b) => a.localeCompare(b, lang));

    // Map to objects with count
    return filtered.map((name) => ({
      name,
      count: composerCounts.get(name) || 0,
    }));
  }, [composers, composerCounts, searchQuery, selectedLetter, lang]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim()) {
      setSelectedLetter(null); // Clear letter filter when searching
    }
  };

  // Handle letter button click
  const handleLetterClick = (letter: string) => {
    setSelectedLetter(letter);
    setSearchQuery(''); // Clear search when selecting letter
    onComposerChange(null); // Clear composer selection
  };

  // Handle "All" button click
  const handleAllClick = () => {
    setSearchQuery('');
    setSelectedLetter(null);
    onComposerChange(null);
  };

  // Handle composer selection
  const handleComposerClick = (composerName: string) => {
    // Toggle: if already selected, uncheck it
    if (selectedComposer === composerName) {
      onComposerChange(null);
    } else {
      onComposerChange(composerName);
    }
  };

  return (
    <div className='flex-1 flex flex-col min-h-0 px-1 py-1.5'>
      <h3 className='my-1.5 text-base font-semibold'>
        {t('mainPage.sidebar.filters.composers')}
      </h3>

      {/* Search Input */}
      <div className='mb-3 mt-1.5'>
        <SearchInput
          testId='composer_filter_search_input'
          size='sm'
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t('mainPage.sidebar.filters.searchComposers')}
        />
      </div>

      {/* Alphabet Buttons */}
      <div className='mb-3 flex flex-wrap gap-1'>
        {/* "All" Button */}
        <button
          data-testid='composer_filter_all_button'
          onClick={handleAllClick}
          className={cn(
            'px-1.5 text-sm font-medium rounded border transition-colors',
            !selectedLetter && !searchQuery
              ? 'bg-zinc-200 border-zinc-200'
              : 'bg-white text-black border-zinc-200 hover:bg-zinc-100'
          )}
        >
          {t('mainPage.sidebar.filters.allComposers')}
        </button>

        {/* Letter Buttons (only available letters) */}
        {availableLetters.map((letter) => (
          <button
            key={letter}
            data-testid={`composer_filter_letter_${letter}`}
            onClick={() => handleLetterClick(letter)}
            className={cn(
              'w-6 h-6 text-sm font-medium rounded border transition-colors',
              selectedLetter === letter
                ? 'bg-zinc-200 border-zinc-200'
                : 'bg-white text-black border-zinc-200 hover:bg-zinc-100'
            )}
          >
            {letter}
          </button>
        ))}
      </div>

      {/* Composer List */}
      <div className='flex-1 overflow-y-auto space-y-2' data-testid='composer_filter_list'>
        {filteredComposers.length === 0 ? (
          <p className='text-sm text-zinc-500 py-2 px-1.5' data-testid='composer_filter_no_results'>
            {t('mainPage.sidebar.filters.noComposersFound')}
          </p>
        ) : (
          filteredComposers.map(({ name, count }) => (
            <label
              key={name}
              data-testid={`composer_filter_item_${name.replace(/[^a-zA-Z0-9а-яА-Я]/g, '_')}`}
              className='mb-0 py-2 px-1.5 flex items-center gap-3 cursor-pointer group'
            >
              <Checkbox
                checked={selectedComposer === name}
                onCheckedChange={() => handleComposerClick(name)}
              />
              <span className='text-sm font-medium select-none'>
                {name} ({count})
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};
